package com.wintermar.inspection.feature.presentation.inspection.answer

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material3.HorizontalDivider
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.navigationevent.NavigationEventInfo
import androidx.navigationevent.compose.NavigationBackHandler
import androidx.navigationevent.compose.rememberNavigationEventState
import com.wintermar.inspection.core.ui.component.datetime.DateTimePickerModal
import com.wintermar.inspection.core.ui.component.image.ImagePickerModal
import com.wintermar.inspection.core.ui.component.location.LocationModal
import com.wintermar.inspection.core.ui.component.modal.ConfirmationDialog
import com.wintermar.inspection.core.ui.component.modal.util.ModalAction
import com.wintermar.inspection.core.ui.theme.AppDimension
import com.wintermar.inspection.core.ui.theme.LocalAppTheme
import com.wintermar.inspection.core.ui.theme.color.model.ThemeColor
import com.wintermar.inspection.core.ui.util.ScrollDirection
import com.wintermar.inspection.core.ui.util.SelectionModel
import com.wintermar.inspection.core.ui.widget.DefaultHorizontalDivider
import com.wintermar.inspection.core.ui.widget.LoadingDialog
import com.wintermar.inspection.feature.domain.model.answer.CompleteAnswerModel
import com.wintermar.inspection.feature.domain.model.inspection.InspectionModel
import com.wintermar.inspection.feature.domain.model.template.InspectionTemplateModel
import com.wintermar.inspection.feature.presentation.inspection.answer.widget.ContentItem
import com.wintermar.inspection.feature.presentation.inspection.answer.widget.ContentItemProps
import com.wintermar.inspection.feature.presentation.inspection.answer.widget.Footer
import com.wintermar.inspection.feature.presentation.inspection.answer.widget.HeaderProgress
import com.wintermar.inspection.feature.presentation.inspection.answer.widget.SectionContent
import com.wintermar.inspection.feature.presentation.inspection.answer.widget.finding_category.FindingCategoryModal
import com.wintermar.inspection.feature.presentation.inspection.answer.widget.guidance.GuidanceModal
import com.wintermar.inspection.feature.presentation.inspection.answer.widget.header.Header
import com.wintermar.inspection.feature.presentation.inspection.answer.widget.incomplete.IncompleteModal
import com.wintermar.inspection.feature.presentation.inspection.answer.widget.notes.NotesModal
import com.wintermar.inspection.feature.presentation.inspection.answer.widget.page_info.PageInfoModal
import com.wintermar.inspection.feature.util.finding_category.toVariant
import com.wintermar.inspection.util.Constant
import inspection.composeapp.generated.resources.Res
import inspection.composeapp.generated.resources.add_files
import inspection.composeapp.generated.resources.cancel
import inspection.composeapp.generated.resources.complete_remaining_item
import inspection.composeapp.generated.resources.exit
import inspection.composeapp.generated.resources.exit_inspection
import inspection.composeapp.generated.resources.maximum_file
import inspection.composeapp.generated.resources.save_progress
import inspection.composeapp.generated.resources.select_category
import inspection.composeapp.generated.resources.select_location
import inspection.composeapp.generated.resources.submitting_inspection
import inspection.composeapp.generated.resources.your_progress_is_saved
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

@Composable
fun InspectionAnswerScreen(
    paddingValues: PaddingValues,
    inspectionId: String,
    viewModel: InspectionAnswerViewModel = koinViewModel { parametersOf(inspectionId) },
    searchResultData: InspectionTemplateModel? = null,
    searchSelectedResult: Triple<String?, String?, String?>? = null, //pageId, sectionId, questionId
    color: ThemeColor = LocalAppTheme.current.color,
    dimension: AppDimension = LocalAppTheme.current.dimension,
    onClose: (InspectionModel) -> Unit,
    toCompleteScreen: (
        CompleteAnswerModel
    ) -> Unit,
    toSearchQuestion: (InspectionTemplateModel) -> Unit
) {
    val state = viewModel.state
    val listState = rememberLazyListState()
    val scope = rememberCoroutineScope()
    var showHeader by remember { mutableStateOf(true) }
    var showExitConfirmation by remember { mutableStateOf(false) }
    val navigationEventState = rememberNavigationEventState<NavigationEventInfo>(
        currentInfo = NavigationEventInfo.None,
    )
    var isMonitorScreen by remember { mutableStateOf(false) }
    var fillWidth by remember { mutableStateOf(1f) }

    fun headerIndexOf(headerIndex: Int): Int {
        var index = 0
        for (i in 0 until headerIndex) {
            index += 1 // header itself
            if (state.page.sections[i].isExpanded) {
                index += state.page.sections[i].questions.size
            }
        }
        return index
    }

    fun calculateQuestionItemIndex(questionId: String): Int {
        var itemIndex = 0

        for (section in state.page.sections) {
            itemIndex += 1  // stickyHeader

            if (section.isExpanded) {
                val qIndex = section.questions.indexOfFirst { it.id == questionId }
                if (qIndex != -1) {
                    return itemIndex + qIndex
                }
                itemIndex += section.questions.size
            }
        }
        return -1
    }

    fun handleExpandSection(sectionId: String, isExpand: Boolean, position: Int) {
        state.expandSection(sectionId, isExpand)
        scope.launch {
            val headerIdx = headerIndexOf(position)
            listState.animateScrollToItem(headerIdx)
        }
    }

    LaunchedEffect(Unit) {
        setupContent { isMonitorScreenContent, fillWidthContent ->
            isMonitorScreen = isMonitorScreenContent
            fillWidth = fillWidthContent
        }
        viewModel.navigationEvent.collect { event ->
            when (event) {
                is InspectionAnswerEvent.NavigateToLanding -> onClose(event.inspectionModel)
                is InspectionAnswerEvent.NavigateToSearchQuestion -> toSearchQuestion(event.inspectionTemplate)
            }
        }
    }

    LaunchedEffect(searchSelectedResult) {
        searchSelectedResult?.let { result ->
            state.onGoToSpecificContent(
                selectedPageId = result.first,
                selectedSectionId = result.second,
                selectedQuestionId = result.third,
                onScrollSection = { position, sectionId ->
                    handleExpandSection(
                        sectionId = sectionId,
                        isExpand = true,
                        position = position
                    )
                },
                onScrollQuestion = { questionId ->
                    scope.launch {
                        val itemIndex = calculateQuestionItemIndex(questionId)
                        if (itemIndex != -1) {
                            listState.animateScrollToItem(itemIndex, scrollOffset = -100)
                        }
                    }


                }
            )
        }
    }

    LaunchedEffect(searchResultData) {
        searchResultData?.let {
            state.onUpdateSearchData(
                inspectionTemplate = it,
            )
        }
    }

    NavigationBackHandler(state = navigationEventState, onBackCompleted = {
        showExitConfirmation = true
    })

    ScrollDirection(
        listState = listState,
        onScrollUp = {
            showHeader = true
        },
        onScrollDown = {
            showHeader = false
        }
    )

    LaunchedEffect(state.completeScreen) {
        if (state.completeScreen) {
            toCompleteScreen(
                state.completeAnswer
            )
        }
    }

    Box(
        modifier = Modifier.fillMaxSize()
            .background(color.bg.normal)
            .padding(top = paddingValues.calculateTopPadding()),
        contentAlignment = Alignment.BottomCenter
    ) {
        Column(
            modifier = Modifier.fillMaxSize(),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                AnimatedVisibility(
                    visible = showHeader
                ) {
                    Header(
                        templateName = state.template.name,
                        connectivityMonitorStatus = state.connectivityMonitorStatus,
                        onClose = {
                            showExitConfirmation = true
                        },
                        onSaveProgress = {
                            state.saveProgress()
                        },
                        onSearch = {
                            state.onSearchClick()
                        }
                    )
                }

                DefaultHorizontalDivider()
                Column(
                    modifier = Modifier.fillMaxWidth(fillWidth)
                ) {
                    HeaderProgress(state = state, withBorder = true)
                }

                if (!isMonitorScreen) HorizontalDivider(
                    modifier = Modifier.fillMaxWidth(),
                    color = color.border.subtle
                )
            }

            LazyColumn(
                state = listState,
                modifier = Modifier
                    .fillMaxHeight()
                    .fillMaxWidth()
                    .padding(
                        bottom = paddingValues.calculateBottomPadding() + dimension.spacing.space9
                    ),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                state.page.sections.forEachIndexed { idx, section ->
                    stickyHeader(key = section.id) {
                        SectionContent(
                            text = section.title,
                            expanded = section.isExpanded,
                            withRadius = isMonitorScreen,
                            modifier = Modifier.fillMaxWidth(fillWidth).animateItem(),
                            onClick = {
                                handleExpandSection(
                                    sectionId = section.id,
                                    isExpand = !section.isExpanded,
                                    position = idx
                                )
                            }
                        )
                    }

                    if (section.isExpanded) {
                        items(items = section.questions, key = { it.id }) {
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth(fillWidth)
                                    .background(color.surface.normal)
                                    .animateItem()
                            ) {
                                ContentItem(
                                    ContentItemProps(
                                        showActionButton = state.pageOrder != 0,
                                        isInternalAudit = state.isInternalAudit,
                                        item = it,
                                        vesselList = state.vessels,
                                        onChooseAnswer = { answerId ->
                                            state.setAnswerOption(
                                                questionId = it.id,
                                                answerOptionId = answerId
                                            )
                                        },
                                        onClickVessel = { id, value ->
                                            state.setAnswerVessel(
                                                questionId = it.id,
                                                vesselId = id,
                                                vesselName = value
                                            )
                                        },
                                        onSetAnswerTextChanged = { value ->
                                            state.setAnswerTextChanged(
                                                questionId = it.id,
                                                text = value
                                            )
                                        },
                                        onDeleteNotes = {
                                            state.onSaveNotes(
                                                questionId = it.id,
                                                notes = ""
                                            )
                                        },
                                        onDeleteFiles = {
                                            state.onSaveFiles(
                                                questionId = it.id,
                                                files = emptyList()
                                            )
                                        },
                                        onShowModalGuidance = {
                                            state.onShowModalGuidance(true, it)
                                        },
                                        onShowModalCalendar = {
                                            state.onShowModalDate(
                                                show = true,
                                                questionId = it.id,
                                                dateIso = it.answer.answerDateIso,
                                                title = it.questionText
                                            )
                                        },
                                        onShowModalLocation = {
                                            state.onShowModalLocation(
                                                show = true,
                                                questionId = it.id,
                                                name = it.answer.answerLocation.name
                                            )
                                        },
                                        onShowModalNotes = {
                                            state.onShowModalNotes(
                                                show = true,
                                                questionId = it.id,
                                                notes = it.answer.notes
                                            )
                                        },
                                        onShowModalFiles = {
                                            state.onShowModalMedia(
                                                show = true,
                                                questionId = it.id,
                                                files = it.answer.files
                                            )
                                        },
                                        onShowFindingCategory = { findingCategory ->
                                            state.onShowModalFindingCategory(
                                                show = true,
                                                questionId = it.id,
                                                findingCategory = findingCategory
                                            )
                                        }
                                    )
                                )

                                HorizontalDivider(
                                    modifier = Modifier.fillMaxWidth(),
                                    color = color.border.subtle
                                )
                            }
                        }
                    }
                }

                item {
                    Spacer(modifier = Modifier.height(dimension.spacing.space12))
                }
            }
        }

        Footer(
            state = state,
            listState = listState,
            scope = scope,
            paddingValues = paddingValues,
            fillWidth = fillWidth
        )

        ImagePickerModal(
            title = stringResource(Res.string.add_files),
            description = stringResource(Res.string.maximum_file, Constant.MAX_FILES),
            files = state.modalFiles.files,
            show = state.modalFiles.show,
            onDismiss = {
                state.onShowModalMedia(false)
            },
            onSave = {
                state.onSaveModalFiles(it)
            }
        )

        NotesModal(
            notes = state.modalNotes.notes,
            show = state.modalNotes.show,
            onDismiss = {
                state.onShowModalNotes(show = false)
            },
            onSave = {
                state.onSaveModalNotes(it)
            }
        )

        FindingCategoryModal(
            title = stringResource(Res.string.select_category),
            show = state.modalFindingCategory.show,
            selectedFindingCategory = state.modalFindingCategory.findingCategory,
            referenceItems = state.findingCategoryOptions.map {
                SelectionModel(
                    id = it.name,
                    value = it.toVariant().label
                )
            },
            onDismiss = {
                state.onShowModalFindingCategory(show = false)
            },
            onSelectedFindingCategory = {
                state.onSaveModalFindingCategory(it)
            }
        )

        DateTimePickerModal(
            title = state.modalDate.title,
            dateIsoUtc0 = state.modalDate.dateIso,
            show = state.modalDate.show,
            onDismiss = {
                state.onShowModalDate(show = false, questionId = state.modalDate.questionId)
            },
            onSave = {
                state.onSaveModalDate(it)
            }
        )

        LocationModal(
            title = stringResource(Res.string.select_location),
            location = state.modalLocation.name,
            show = state.modalLocation.show,
            onDismiss = {
                state.onShowModalLocation(show = false)
            },
            onSave = { name, location ->
                state.onSaveModalLocation(name, location)
            }
        )

        PageInfoModal(
            show = state.modalPageInfo,
            currentPage = state.pageOrder,
            data = state.template,
            onDismiss = {
                state.onShowModalPageInfo(false)
            },
            onClick = { pageOrder ->
                state.onGoToSpecificPage(pageOrder)
                state.onShowModalPageInfo(false)
            }
        )

        GuidanceModal(
            show = state.modalGuidance.show,
            question = state.modalGuidance.question?.questionText ?: "",
            guidance = state.modalGuidance.question?.guidelines ?: "",
            onDismiss = {
                state.onShowModalGuidance(false)
            }
        )

        IncompleteModal(
            show = state.modalInComplete.show,
            unAnswerList = state.modalInComplete.unAnswerList,
            needDocumentList = state.modalInComplete.needDocumentList,
            action = ModalAction.default().copy(
                showAction = true,
                label = stringResource(Res.string.complete_remaining_item),
                secondLabel = stringResource(Res.string.save_progress),
                onClick = {
                    state.onShowModalInComplete(show = false)
                    state.onCompleteRemainingItem()
                },
                secondOnClick = {
                    state.onShowModalInComplete(show = false)
                    state.saveProgress()
                }
            ),
            onDismiss = {
                state.onShowModalInComplete(show = false)
            }
        )

        ConfirmationDialog(
            show = showExitConfirmation,
            title = stringResource(Res.string.exit_inspection),
            message = stringResource(Res.string.your_progress_is_saved),
            action = ModalAction.default().copy(
                showAction = true,
                isDangerAction = true,
                withBackgroundHeader = true,
                widthButtonBasedOnText = true,
                label = stringResource(Res.string.exit),
                secondLabel = stringResource(Res.string.cancel),
                onClick = {
                    showExitConfirmation = false
                    state.saveProgress()
                },
                secondOnClick = {
                    showExitConfirmation = false
                }),
            onDismiss = {
                showExitConfirmation = false
            },
        )

        LoadingDialog(
            isLoading = state.isLoadingCompleteInspection,
            message = stringResource(Res.string.submitting_inspection)
        )

        LoadingDialog(
            isLoading = state.isLoadingDetailTemplate
        )
    }
}

expect fun setupContent(
    onLoad: (isMonitorScreen: Boolean, fillWidth: Float) -> Unit
)
